import { CartLinesUpdateEvent } from '@shopify/events';

/** The option picked on this product page (the add-to-cart form's variant id). */
function currentVariantId(from) {
  const scope = from.closest('.shopify-section, section, main') || document;
  const input =
    scope.querySelector('form[action*="/cart/add"] [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
  const id = input && String(input.value || '').trim();
  return id && /^\d+$/.test(id) ? id : '';
}

/** Shopify's money format of the box ("$1,234,567.89", "€1.234.567,89"), read from a sample rendered by Liquid. */
function moneyFormatter(box) {
  const sample = box.dataset.moneySample || '';
  const match = /1(\D?)234(?:\1)567(?:(\D)(\d{2}))?/.exec(sample) || /1(\D?)234(?:\1)568()/.exec(sample);
  if (!match) return (cents) => (cents / 100).toFixed(2);
  const [whole, thousands, decimal] = match;
  const prefix = sample.slice(0, match.index);
  const suffix = sample.slice(match.index + whole.length);
  return (cents) => {
    const withDecimals = decimal !== undefined;
    const [int, frac] = (withDecimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100))).split('.');
    return prefix + int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (withDecimals ? decimal + frac : '') + suffix;
  };
}

function packPrices(box) {
  try {
    return JSON.parse(box.querySelector('[data-pack-prices]')?.textContent || '{}');
  } catch {
    return null;
  }
}

/** Photo and name of one slot from its select. */
function syncSlot(select, prices) {
  const row = prices[select.value];
  const slot = select.closest('.bh-pick');
  if (!row || !slot) return;
  const img = slot.querySelector('[data-slot-img]');
  if (img && row.img) img.src = row.img;
}

/** Until the customer changes a slot, the slots start with the option picked on the page, then the other ones. */
function defaultSlots(box, prices, pickedId) {
  const selects = [...box.querySelectorAll('[data-pack-slot]')];
  if (!selects.length) return;
  const order = [...selects[0].options].filter((o) => !o.disabled).map((o) => o.value);
  const pool = pickedId && prices[pickedId]?.ok ? [pickedId, ...order.filter((id) => id !== pickedId)] : order;
  if (!pool.length) return;
  selects.forEach((select, i) => {
    select.value = pool[i % pool.length];
    syncSlot(select, prices);
  });
}

/** The pack sizes of a box: their radio, units and the share still charged (basis points). */
function packTiers(box) {
  return [...box.querySelectorAll('[data-pack-tier][data-qty]')].map((input) => ({
    input,
    qty: Number(input.dataset.qty) || 0,
    ratio: Number(input.dataset.ratio) || 10000,
    card: input.closest('label'),
  }));
}

/**
 * Prices every size from the units it would add (its first slots, or the option picked on the page), every unit
 * at that size's percentage like the cart discount. The picked size sets the visible rows and the button.
 */
function repriceTiers(box, prices, pickedId) {
  const format = moneyFormatter(box);
  const selects = [...box.querySelectorAll('[data-pack-slot]')];
  const unitIds = (qty) => (selects.length ? selects.slice(0, qty).map((s) => s.value) : Array(qty).fill(pickedId));
  const picked = box.querySelector('[data-pack-tier]:checked');
  // A pack size or a set (its fixed pieces, priced by Liquid) shows its rows and button instead of Add to cart.
  box.toggleAttribute('data-on', !!picked?.dataset.qty || !!picked?.hasAttribute('data-set'));
  const one = prices[pickedId];
  if (one) box.querySelectorAll('[data-one-now]').forEach((node) => {
    node.innerHTML = one.one;
  });
  const button = box.querySelector('[data-bh-pack]');
  for (const tier of packTiers(box)) {
    let full = 0;
    let sale = 0;
    let ok = true;
    for (const id of unitIds(tier.qty)) {
      const row = prices[id];
      if (!row) {
        ok = false;
        continue;
      }
      full += row.cents;
      sale += Math.round((row.cents * tier.ratio) / 10000);
      if (!row.ok) ok = false;
    }
    const set = (selector, text) => {
      tier.card?.querySelectorAll(selector).forEach((node) => {
        node.textContent = text;
      });
    };
    set('[data-tier-now]', format(sale));
    set('[data-tier-was]', format(full));
    set('[data-tier-each]', format(Math.round(sale / Math.max(1, tier.qty))));
    if (tier.input !== picked) continue;
    selects.forEach((select, i) => {
      const row = select.closest('.bh-pick');
      if (row) row.hidden = i >= tier.qty;
    });
    box.querySelectorAll('[data-pick-n]').forEach((node) => {
      node.textContent = String(tier.qty);
    });
    if (!button) continue;
    button.dataset.qty = String(tier.qty);
    button.dataset.label = (button.dataset.labelTpl || '').replace('[n]', String(tier.qty));
    if (button.dataset.added) continue;
    const label = button.querySelector('.bh-pack__label');
    if (label) label.textContent = button.dataset.label;
    button.disabled = !ok;
  }
}

function pickedVariant(box, prices) {
  const button = box.querySelector('[data-bh-pack]');
  return (button && currentVariantId(button)) || Object.keys(prices)[0] || '';
}

/** Quantity pack boxes follow the option picked on the page until the customer changes a slot. */
function refreshQuantityPacks() {
  for (const box of document.querySelectorAll('[data-bh-pack-qty]')) {
    const prices = packPrices(box);
    if (!prices) continue;
    const id = pickedVariant(box, prices);
    if (box.hasAttribute('data-mix') && !box.dataset.touched) defaultSlots(box, prices, id);
    repriceTiers(box, prices, id);
  }
}

if (!window.__bhPackQty) {
  window.__bhPackQty = true;
  const later = () => [50, 400, 1200].forEach((ms) => window.setTimeout(refreshQuantityPacks, ms));
  document.addEventListener('change', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    const box = el?.closest('[data-bh-pack-qty]');
    if (!el || !box || !el.matches('[data-pack-tier], [data-pack-slot]')) return later();
    const prices = packPrices(box);
    if (!prices) return;
    if (el.matches('[data-pack-slot]')) {
      box.dataset.touched = 'true';
      syncSlot(el, prices);
    }
    repriceTiers(box, prices, pickedVariant(box, prices));
  });
  document.addEventListener('variant:update', later);
  later();
}

if (!window.__bhPack) {
  window.__bhPack = true;

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bh-pack]') : null;
    if (!button || button.disabled) return;

    const error = button.parentElement?.querySelector('.bh-pack__error');
    const errorText = error?.querySelector('.bh-pack__error-text');
    // A quantity pack adds the mix chosen in its slots (or `qty` units of the picked option when the product has
    // one option); a set pack adds one of each piece.
    const qty = Number(button.dataset.qty) || 0;
    const slots = [...(button.closest('[data-bh-pack-qty]')?.querySelectorAll('[data-pack-slot]') ?? [])].slice(0, qty).map((s) => s.value);
    const counts = new Map();
    if (qty > 1 && slots.length) for (const id of slots) counts.set(id, (counts.get(id) || 0) + 1);
    else if (qty > 1) counts.set(currentVariantId(button) || button.dataset.ids.split(',')[0], qty);
    else for (const id of button.dataset.ids.split(',').filter(Boolean)) counts.set(id, 1);
    const lines = [...counts].map(([id, quantity]) => ({ id, quantity }));
    const units = lines.reduce((n, line) => n + line.quantity, 0);
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
    const sectionList = sections.join(',');
    const deferred = CartLinesUpdateEvent.createPromise();

    const label = button.querySelector('.bh-pack__label');
    const write = (text) => {
      if (label) label.textContent = text;
      else button.textContent = text;
    };
    let addedTimer = 0;
    const showAdded = () => {
      button.dataset.added = 'true';
      clearTimeout(addedTimer);
      addedTimer = window.setTimeout(() => {
        delete button.dataset.added;
      }, 1400);
    };
    const hideAdded = () => {
      clearTimeout(addedTimer);
      delete button.dataset.added;
    };

    button.disabled = true;
    showAdded();
    if (error) error.hidden = true;

    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
        promise: deferred.promise,
      }),
    );

    const fail = (reason) => {
      deferred.reject(new Error('pack'));
      button.disabled = false;
      hideAdded();
      write(button.dataset.label || '');
      if (error) {
        error.hidden = false;
        const message = (typeof reason === 'string' && reason.trim()) || button.dataset.error || '';
        if (errorText) errorText.textContent = message;
        else error.textContent = message;
      }
    };

    const done = (cartJson) => {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
        detail: {
          items: cartJson.items,
          source: 'bh-pack',
          sourceId: button.dataset.productId,
          itemCount: units,
          productId: button.dataset.productId,
          sections: cartJson.sections,
          didError: false,
        },
      });
      button.disabled = false;
      write(button.dataset.label || '');
    };

    try {
      const addResponse = await fetch(button.dataset.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
          sections: sectionList,
        }),
      });
      const added = await addResponse.json();

      if (!addResponse.ok || added.status || added.errors) {
        // Shopify says why (for example only 1 left of an option): show that instead of "try again".
        fail(added.description || added.message);
        return;
      }

      // No code: the automatic discount applies by itself. The add response is not a full cart
      // (no currency), so read the cart and reuse the section HTML from the add response.
      const code = (button.dataset.code || '').trim();
      if (!code) {
        const cart = await fetch(`${button.dataset.root}cart.js`, { headers: { Accept: 'application/json' } }).then(
          (response) => response.json(),
        );
        cart.sections = added.sections;
        done(cart);
        return;
      }

      const updated = await fetch(button.dataset.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code, sections: sectionList }),
      }).then((response) => response.json());

      const applied = (updated.discount_codes || []).some(
        (entry) => entry.code === code && entry.applicable,
      );
      if (!applied) {
        const back = encodeURIComponent(button.dataset.cart || '/cart');
        window.location.assign(`${button.dataset.root}discount/${code}?redirect=${back}`);
        return;
      }

      done(updated);
    } catch {
      fail();
    }
  });
}
